import AddIcon from "@mui/icons-material/Add";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SearchIcon from "@mui/icons-material/Search";
import TrackChangesOutlinedIcon from "@mui/icons-material/TrackChangesOutlined";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, InputAdornment, MenuItem, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";

import { OUTIL_COLORS, paperSx, tableHeaderSx } from "./OutilContainer.styles";

const EMPTY_COMMUNICATION = {
  comments: "",
  evaluation: "",
  formSupport: "",
  objectives: "",
  periodicity: "",
  target: "",
  theme: "",
  type: "INTERNE",
  whoRealizes: "",
};

const SEARCH_FIELDS = [
  "type", "theme", "objectives", "whoRealizes", "target",
  "formSupport", "evaluation", "periodicity", "comments",
];

const COLUMNS = [
  ["Type", 110], ["Thème", 180], ["Objectifs", 190],
  ["Qui réalise", 155], ["Pour qui", 155], ["Forme", 160],
  ["Évaluation", 180], ["Périodicité", 135],
  ["Commentaires", 180], ["Actions", 100],
];

const normalize = (value) => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLocaleLowerCase("fr");

const createId = () =>
  `communication-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const textareaSx = {
  backgroundColor: "#fff",
  border: `1px solid ${OUTIL_COLORS.border}`,
  borderRadius: "6px",
  boxSizing: "border-box",
  color: OUTIL_COLORS.text,
  fontFamily: "'DM Sans', sans-serif",
  fontSize: 11.5,
  lineHeight: 1.45,
  minHeight: 68,
  outline: "none",
  p: 1,
  resize: "vertical",
  width: "100%",
  "&:focus": {
    borderColor: OUTIL_COLORS.navy,
    boxShadow: "0 0 0 2px rgba(23,61,97,.12)",
  },
};

const goldButtonSx = {
  backgroundColor: OUTIL_COLORS.gold,
  color: OUTIL_COLORS.text,
  fontSize: 10,
  fontWeight: 700,
  textTransform: "none",
  "&:hover": { backgroundColor: "#c79c2f" },
};

const sectionSx = {
  backgroundColor: "#f8fafc",
  border: `1px solid ${OUTIL_COLORS.border}`,
  borderRadius: "8px",
  p: 1.4,
};

const FieldLabel = ({ children, htmlFor }) => (
  <Typography
    color="#526b84"
    component="label"
    display="block"
    fontSize={9.5}
    fontWeight={700}
    htmlFor={htmlFor}
    letterSpacing=".04em"
    mb={0.45}
    textTransform="uppercase"
  >
    {children}
  </Typography>
);

const TextareaField = ({ field, label, onChange, rows = 3, value }) => (
  <Box>
    <FieldLabel htmlFor={`communication-${field}`}>{label}</FieldLabel>
    <Box
      aria-label={label}
      component="textarea"
      id={`communication-${field}`}
      onChange={(event) => onChange(field, event.target.value)}
      rows={rows}
      sx={textareaSx}
      value={value || ""}
    />
  </Box>
);

const FormSection = ({ children, icon, title }) => (
  <Paper elevation={0} sx={sectionSx}>
    <Typography
      color={OUTIL_COLORS.navy}
      fontSize={10.5}
      fontWeight={800}
      sx={{
        borderBottom: `1px solid ${OUTIL_COLORS.border}`,
        mb: 1.2,
        pb: 0.75,
      }}
      textTransform="uppercase"
    >
      {icon}
      {title}
    </Typography>
    {children}
  </Paper>
);

const CommunicationDialog = ({
  form,
  onChange,
  onClose,
  onDelete,
  onSave,
  open,
}) => (
  <Dialog
    fullWidth
    maxWidth="md"
    onClose={onClose}
    open={open}
    PaperProps={{ sx: { borderRadius: "14px", maxHeight: "92vh" } }}
  >
    <DialogTitle
      sx={{
        alignItems: "center",
        borderBottom: `1px solid ${OUTIL_COLORS.border}`,
        display: "flex",
        fontFamily: "Georgia, serif",
        fontSize: 17,
        fontWeight: 700,
        justifyContent: "space-between",
        px: 2.2,
        py: 1.4,
      }}
    >
      <Box component="span">
        <CampaignOutlinedIcon
          sx={{ color: "#d54873", fontSize: 19, mr: 0.8, verticalAlign: "middle" }}
        />
        Plan de communication — Fiche communication
      </Box>
      <Button
        color="error"
        onClick={onClose}
        size="small"
        startIcon={<CloseIcon />}
        sx={{ textTransform: "none" }}
        variant="outlined"
      >
        Fermer
      </Button>
    </DialogTitle>

    <DialogContent sx={{ p: "18px !important" }}>
      <Box sx={{ display: "grid", gap: 1.2 }}>
        <Paper
          elevation={0}
          sx={{
            alignItems: "center",
            backgroundColor: "#edf3f8",
            border: `1px solid ${OUTIL_COLORS.border}`,
            borderRadius: "7px",
            display: "flex",
            justifyContent: "space-between",
            px: 1.4,
            py: 1,
          }}
        >
          <Typography color={OUTIL_COLORS.textMuted} fontSize={10.5}>
            {form.id ? "Modification de l'entrée sélectionnée" : "Nouvelle entrée de communication"}
          </Typography>
          {form.id && (
            <Button
              color="error"
              onClick={onDelete}
              size="small"
              startIcon={<DeleteOutlineIcon />}
              sx={{ fontSize: 10, textTransform: "none" }}
              variant="contained"
            >
              Supprimer cette entrée
            </Button>
          )}
        </Paper>

        <FormSection
          icon={<AttachFileOutlinedIcon sx={{ fontSize: 14, mr: 0.6, verticalAlign: "middle" }} />}
          title="Pièces jointes (0)"
        >
          <Typography color={OUTIL_COLORS.textMuted} fontSize={10.5}>
            Aucune pièce jointe
          </Typography>
          <Tooltip title="Ajout de pièce jointe disponible ultérieurement">
            <span>
              <Button
                disabled
                size="small"
                startIcon={<AttachFileOutlinedIcon />}
                sx={{ fontSize: 10.5, mt: 0.5, textTransform: "none" }}
              >
                Ajouter une pièce jointe
              </Button>
            </span>
          </Tooltip>
        </FormSection>

        <FormSection
          icon={<EditOutlinedIcon sx={{ color: "#d49b17", fontSize: 14, mr: 0.6, verticalAlign: "middle" }} />}
          title="Identification"
        >
          <Box sx={{ display: "grid", gap: 1.1, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
            <Box>
              <FieldLabel htmlFor="communication-type">Type de communication</FieldLabel>
              <TextField
                fullWidth
                id="communication-type"
                onChange={(event) => onChange("type", event.target.value)}
                select
                size="small"
                value={form.type || "INTERNE"}
              >
                <MenuItem value="INTERNE">Interne</MenuItem>
                <MenuItem value="EXTERNE">Externe</MenuItem>
                <MenuItem value="MIXTE">Interne et externe</MenuItem>
              </TextField>
            </Box>
            <Box>
              <FieldLabel htmlFor="communication-periodicity">Périodicité</FieldLabel>
              <TextField
                fullWidth
                id="communication-periodicity"
                onChange={(event) => onChange("periodicity", event.target.value)}
                size="small"
                value={form.periodicity || ""}
              />
            </Box>
            <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
              <TextareaField field="theme" label="Thème / sujet" onChange={onChange} value={form.theme} />
            </Box>
          </Box>
        </FormSection>

        <FormSection
          icon={<TrackChangesOutlinedIcon sx={{ color: "#d54873", fontSize: 14, mr: 0.6, verticalAlign: "middle" }} />}
          title="Objectifs et cibles"
        >
          <Box sx={{ display: "grid", gap: 1.1, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
            <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
              <TextareaField field="objectives" label="Objectifs de la communication" onChange={onChange} value={form.objectives} />
            </Box>
            <TextareaField field="whoRealizes" label="Qui réalise" onChange={onChange} rows={2} value={form.whoRealizes} />
            <TextareaField field="target" label="Pour qui (cible)" onChange={onChange} rows={2} value={form.target} />
            <TextareaField field="formSupport" label="Forme / support" onChange={onChange} value={form.formSupport} />
          </Box>
        </FormSection>

        <FormSection
          icon={<DescriptionOutlinedIcon sx={{ color: "#1b9b68", fontSize: 14, mr: 0.6, verticalAlign: "middle" }} />}
          title="Évaluation"
        >
          <Box sx={{ display: "grid", gap: 1.1, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
            <TextareaField field="evaluation" label="Évaluation / résultats" onChange={onChange} value={form.evaluation} />
            <TextareaField field="comments" label="Commentaires" onChange={onChange} value={form.comments} />
          </Box>
        </FormSection>
      </Box>
    </DialogContent>

    <DialogActions sx={{ borderTop: `1px solid ${OUTIL_COLORS.border}`, px: 2.2, py: 1.25 }}>
      <Button onClick={onClose} sx={{ color: OUTIL_COLORS.text, textTransform: "none" }}>
        Annuler
      </Button>
      <Button
        onClick={onSave}
        startIcon={<SaveOutlinedIcon />}
        sx={{
          backgroundColor: "#27884f",
          fontWeight: 700,
          textTransform: "none",
          "&:hover": { backgroundColor: "#1f7040" },
        }}
        variant="contained"
      >
        Valider et sauvegarder
      </Button>
    </DialogActions>
  </Dialog>
);

const PlanCommunication = ({ onChange, value = {} }) => {
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_COMMUNICATION });
  const rows = useMemo(
    () => (Array.isArray(value.communicationRows) ? value.communicationRows : []),
    [value.communicationRows]
  );
  const filteredRows = useMemo(() => {
    const query = normalize(search).trim();
    return query
      ? rows.filter((row) => SEARCH_FIELDS.some((field) => normalize(row[field]).includes(query)))
      : rows;
  }, [rows, search]);

  const setRows = (nextRows) => onChange("communicationRows", nextRows);
  const openCreate = () => {
    setForm({ ...EMPTY_COMMUNICATION });
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setForm({ ...EMPTY_COMMUNICATION, ...row });
    setDialogOpen(true);
  };
  const saveCommunication = () => {
    setRows(form.id
      ? rows.map((row) => (row.id === form.id ? { ...form } : row))
      : [...rows, { ...form, id: createId() }]);
    setDialogOpen(false);
  };
  const deleteCommunication = () => {
    if (form.id) setRows(rows.filter((row) => row.id !== form.id));
    setDialogOpen(false);
  };

  return (
    <>
      <Paper aria-label="Plan de communication" elevation={0} role="region" sx={{ ...paperSx, overflow: "hidden" }}>
        <Box sx={{ alignItems: "center", backgroundColor: OUTIL_COLORS.panelHeader, borderBottom: `1px solid ${OUTIL_COLORS.border}`, display: "flex", justifyContent: "space-between", minHeight: 48, px: 1.75 }}>
          <Typography color={OUTIL_COLORS.navy} fontSize={12} fontWeight={700}>
            <CampaignOutlinedIcon sx={{ color: "#d54873", fontSize: 16, mr: 0.7, verticalAlign: "middle" }} />
            Plan de communication — §7.4
          </Typography>
          <Button onClick={openCreate} size="small" startIcon={<AddIcon />} sx={goldButtonSx} variant="contained">
            Ajouter
          </Button>
        </Box>

        <Box sx={{ alignItems: "center", backgroundColor: "#f5f8fb", border: `1px solid ${OUTIL_COLORS.border}`, borderRadius: "6px", display: "flex", gap: 0.7, mx: 1.5, my: 1, px: 1.2, py: 0.8 }}>
          <DescriptionOutlinedIcon sx={{ color: OUTIL_COLORS.gold, fontSize: 16 }} />
          <Typography color={OUTIL_COLORS.textMuted} fontSize={10.5}>
            Cliquez sur une ligne pour ouvrir la fiche détail complète.
          </Typography>
        </Box>

        <Box sx={{ alignItems: "center", backgroundColor: "#edf3f8", display: "flex", flexWrap: "wrap", gap: 1, px: 1.5, py: 1 }}>
          <Typography color={OUTIL_COLORS.textMuted} fontSize={9.5} fontWeight={700}>
            RECHERCHE :
          </Typography>
          <TextField
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Filtrer toutes les colonnes..."
            size="small"
            sx={{ flex: 1, minWidth: 220, "& .MuiInputBase-root": { backgroundColor: "#fff", fontSize: 10.5, height: 34 } }}
            value={search}
          />
          <Typography color={OUTIL_COLORS.textMuted} fontSize={10}>
            {filteredRows.length} / {rows.length} entrée(s)
          </Typography>
          <Button
            disabled={!search}
            onClick={() => setSearch("")}
            size="small"
            startIcon={<RestartAltIcon />}
            sx={{ color: OUTIL_COLORS.navy, fontSize: 10, fontWeight: 700, textTransform: "none" }}
          >
            Réinit.
          </Button>
        </Box>

        <TableContainer sx={{ overflowX: "auto" }}>
          <Table aria-label="Tableau du plan de communication" size="small" sx={{ minWidth: 1450, tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                {COLUMNS.map(([label, width]) => (
                  <TableCell key={label} sx={{ ...tableHeaderSx, width }}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!filteredRows.length ? (
                <TableRow>
                  <TableCell align="center" colSpan={COLUMNS.length} sx={{ color: "#8091a3", fontSize: 11, py: 2.2 }}>
                    Aucune communication enregistrée — cliquez sur Ajouter.
                  </TableCell>
                </TableRow>
              ) : filteredRows.map((row) => {
                const values = [
                  row.theme, row.objectives, row.whoRealizes, row.target,
                  row.formSupport, row.evaluation, row.periodicity, row.comments,
                ];
                return (
                  <TableRow hover key={row.id} onClick={() => openEdit(row)} sx={{ cursor: "pointer", "&:nth-of-type(even)": { backgroundColor: "#f7f9fc" } }}>
                    <TableCell>
                      <Chip label={row.type || "INTERNE"} size="small" sx={{ backgroundColor: "#e7edf4", color: OUTIL_COLORS.navy, fontSize: 9.5, fontWeight: 700 }} />
                    </TableCell>
                    {values.map((cell, index) => (
                      <TableCell key={COLUMNS[index + 1][0]}>{cell || "—"}</TableCell>
                    ))}
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Entrée sauvegardée localement">
                          <IconButton
                            aria-label="Entrée sauvegardée localement"
                            onClick={(event) => event.stopPropagation()}
                            size="small"
                            sx={{ backgroundColor: "#27884f", borderRadius: "6px", color: "#fff" }}
                          >
                            <SaveOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Modifier">
                          <IconButton
                            aria-label="Modifier la communication"
                            onClick={(event) => { event.stopPropagation(); openEdit(row); }}
                            size="small"
                            sx={{ backgroundColor: OUTIL_COLORS.navy, borderRadius: "6px", color: "#fff" }}
                          >
                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <CommunicationDialog
        form={form}
        onChange={(field, nextValue) => setForm((current) => ({ ...current, [field]: nextValue }))}
        onClose={() => setDialogOpen(false)}
        onDelete={deleteCommunication}
        onSave={saveCommunication}
        open={dialogOpen}
      />
    </>
  );
};

export default PlanCommunication;